import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Modal } from './Modal';
import { Button } from './ui';

const FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
];

/** Modal que abre a câmera e lê QR ou código de barras (EAN/UPC/Code128). */
export function BarcodeScanner({
  open,
  onClose,
  onResult,
  skipLabel,
  onSkip,
}: {
  open: boolean;
  onClose: () => void;
  onResult: (text: string) => void;
  /** Botão opcional para seguir sem escanear (ex.: cadastrar sem código). */
  skipLabel?: string;
  onSkip?: () => void;
}) {
  const ref = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const stop = async () => {
      const inst = ref.current;
      if (inst?.isScanning) {
        try {
          await inst.stop();
        } catch {
          /* noop */
        }
      }
      ref.current = null;
    };

    const start = async () => {
      setError(null);
      try {
        const inst = new Html5Qrcode('barcode-reader', { formatsToSupport: FORMATS, verbose: false });
        ref.current = inst;
        await inst.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 170 } },
          (text) => {
            if (cancelled) return;
            cancelled = true;
            void stop().then(() => {
              onResult(text);
              onClose();
            });
          },
          () => undefined,
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Não foi possível acessar a câmera.');
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stop();
    };
  }, [open, onClose, onResult]);

  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Escanear código"
      footer={
        onSkip ? (
          <Button variant="secondary" onClick={onSkip}>
            {skipLabel ?? 'Pular'}
          </Button>
        ) : undefined
      }
    >
      <div
        id="barcode-reader"
        className="mx-auto rounded-xl overflow-hidden bg-bg border border-border"
        style={{ maxWidth: 320 }}
      />
      {error && <p className="text-st-warn text-sm mt-3">{error}</p>}
      <p className="text-xs text-muted mt-3 text-center">Aponte para o código de barras ou QR.</p>
    </Modal>
  );
}
